import { create } from 'zustand';
import { api, ApiError, uploadLane } from '../../lib/api';
import { useMediaList, useOverview } from '../../lib/live';
import { report } from '../../lib/telemetry';
import { fastStart } from './fastStart';

// Chunk sizes for uploads over the internet: from 4 MiB on a poor line up to the server's 64 MiB.
const MIN_CHUNK = 4 << 20;
const MAX_CHUNK = 64 << 20;

// Resumable, chunked uploads. Two chunks are in flight at once and their size follows the line
// (4-64 MiB); a failed chunk costs only itself, and re-selecting the same file after a reload resumes it.

export type UploadStatus = 'starting' | 'uploading' | 'paused' | 'done' | 'error';

export interface Upload {
  id: string;
  file: File;
  /** What's sent: the file itself, or for an MP4/MOV with its index at the end the same film with
   * the index moved to the front (fastStart.ts), so the server can read it as it arrives. */
  body?: Blob;
  mediaId?: string;
  offset: number;
  status: UploadStatus;
  speed: number; // bytes/s (EWMA)
  error?: string;
  lane?: 'local' | 'internet';
}

const RESUME_KEY = 'vb.resumable';
const fingerprint = (f: File) => `${f.name}:${f.size}:${f.lastModified}`;

function resumeMap(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(RESUME_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function saveResume(f: File, mediaId: string | null) {
  try {
    const m = resumeMap();
    if (mediaId) m[fingerprint(f)] = mediaId;
    else delete m[fingerprint(f)];
    localStorage.setItem(RESUME_KEY, JSON.stringify(m));
  } catch {
    /* ignore */
  }
}

/** How long a finished upload stays in the list (showing ✓) before it clears itself. */
const DONE_LINGER = 5000;

const controllers = new Map<string, AbortController>();
let seq = 0;

interface UploadsState {
  uploads: Upload[];
  add: (files: FileList | File[]) => void;
  pause: (id: string) => void;
  resume: (id: string) => void;
  remove: (id: string) => void;
}

export const useUploads = create<UploadsState>((set, get) => {
  const patch = (id: string, p: Partial<Upload>) =>
    set({ uploads: get().uploads.map((u) => (u.id === id ? { ...u, ...p } : u)) });
  const find = (id: string) => get().uploads.find((u) => u.id === id);

  async function run(id: string) {
    const ctrl = new AbortController();
    controllers.set(id, ctrl);
    let u = find(id);
    if (!u) return;
    try {
      let chunkSize = 8 << 20;
      if (!u.body) {
        const fs = /\.(mp4|m4v|mov)$/i.test(u.file.name) ? await fastStart(u.file).catch((e: Error) => ({ blob: u!.file, moved: false, why: `fast start failed: ${e.message}` })) : null;
        if (fs?.moved) report('info', 'drive', 'upload: index moved to the front', { file: u.file.name, size: u.file.size, why: fs.why });
        else if (fs && fs.why.startsWith('fast start failed')) report('warn', 'drive', 'upload: fast start failed', { file: u.file.name, why: fs.why });
        patch(id, { body: fs?.blob ?? u.file });
        u = find(id)!;
      }
      const body = u.body!;
      if (!u.mediaId) {
        const existing = resumeMap()[fingerprint(u.file)];
        const st = existing ? await api.uploadStatus(existing).catch(() => null) : null;
        if (existing && st && st.status === 'uploading') {
          patch(id, { mediaId: existing, offset: st.offset });
        } else {
          const res = await api.createUpload(u.file.name, body.size);
          chunkSize = res.chunkSize;
          saveResume(u.file, res.media.id);
          patch(id, { mediaId: res.media.id, offset: 0 });
        }
      } else {
        const st = await api.uploadStatus(u.mediaId);
        patch(id, { offset: st.offset });
      }
      patch(id, { status: 'uploading', error: undefined });
      useOverview.getState().refresh();

      // On the host Mac itself, send the bytes straight to the local server.
      let base = (await uploadLane()) ?? undefined;
      patch(id, { lane: base ? 'local' : 'internet' });
      if (base) chunkSize = 16 << 20;

      // Two chunks in flight: while the server writes one, the next is already on its way, so the
      // line never sits idle for a round trip (~80-100 ms to the server, every chunk). Chunk size
      // follows the line: bigger on a fast one (fewer requests), smaller after trouble.
      let size = base ? 16 << 20 : chunkSize;
      let next = u.offset;
      let lastAck = performance.now();
      let failures = 0;
      type Flight = { start: number; end: number; stop: AbortController; p: Promise<{ ok: true; offset: number } | { ok: false; e: unknown }> };
      const inflight: Flight[] = [];
      // After a failure, the chunk queued behind it can't land (the server would hold it for its
      // turn): cancel it, then carry on from what the server has.
      const resync = async () => {
        inflight.forEach((f) => f.stop.abort());
        await Promise.allSettled(inflight.map((f) => f.p));
        inflight.length = 0;
        const st = await api.uploadStatus(find(id)!.mediaId!).catch(() => null);
        const off = st?.offset ?? find(id)!.offset;
        patch(id, { offset: off });
        next = off;
        lastAck = performance.now();
      };
      for (;;) {
        u = find(id);
        if (!u || u.status !== 'uploading' || !u.mediaId) return;
        if (u.offset >= body.size) break;
        while (inflight.length < 2 && next < body.size) {
          const start = next;
          const end = Math.min(start + size, body.size);
          const stop = new AbortController();
          ctrl.signal.addEventListener('abort', () => stop.abort(), { once: true });
          const p = api.uploadChunk(u.mediaId, start, body.slice(start, end), stop.signal, base).then(
            (r) => ({ ok: true as const, offset: r.offset }),
            (e: unknown) => ({ ok: false as const, e }),
          );
          inflight.push({ start, end, stop, p });
          next = end;
        }
        const head = inflight.shift()!;
        const r = await head.p;
        if (r.ok) {
          const now = performance.now();
          const secs = Math.max((now - lastAck) / 1000, 0.001);
          lastAck = now;
          const inst = (head.end - head.start) / secs;
          const cur = find(id);
          patch(id, { offset: r.offset, speed: cur?.speed ? cur.speed * 0.7 + inst * 0.3 : inst });
          if (secs < 2) size = Math.min(MAX_CHUNK, size * 2);
          else if (secs > 8) size = Math.max(MIN_CHUNK, size / 2);
          failures = 0;
          continue;
        }
        const e = r.e;
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError && e.status === 409) {
          await resync(); // a chunk sent before its predecessor failed: start again from what landed
          continue;
        }
        // The server said no for good (the upload failed or is gone, a bad request): resending the
        // same piece won't change that, so stop and show its reason.
        if (e instanceof ApiError && [400, 404, 410, 413, 415].includes(e.status)) {
          saveResume(u.file, null);
          throw e;
        }
        if (base && !(e instanceof ApiError)) {
          // The local lane went away (e.g. the server restarted on another port): carry on
          // over the internet.
          base = undefined;
          size = chunkSize;
          patch(id, { lane: 'internet' });
          await resync();
          continue;
        }
        if (++failures > 6) throw e;
        size = Math.max(MIN_CHUNK, Math.min(size, chunkSize)); // a flaky line: small chunks are cheap to resend
        await new Promise((res) => setTimeout(res, Math.min(15000, 500 * 2 ** failures)));
        await resync();
      }
      saveResume(u.file, null);
      patch(id, { status: 'done', offset: body.size });
      useMediaList.getState().refresh();
      useOverview.getState().refresh();
      // The title is in the drive now (with its processing progress): clear the finished row.
      setTimeout(() => set({ uploads: get().uploads.filter((x) => x.id !== id) }), DONE_LINGER);
    } catch (e) {
      if (!ctrl.signal.aborted) patch(id, { status: 'error', error: (e as Error).message });
    } finally {
      controllers.delete(id);
    }
  }

  return {
    uploads: [],
    add: (files) => {
      const added: Upload[] = Array.from(files).map((file) => ({
        id: `u${++seq}`,
        file,
        offset: 0,
        status: 'starting',
        speed: 0,
      }));
      set({ uploads: [...added, ...get().uploads] });
      added.forEach((u) => run(u.id));
    },
    pause: (id) => {
      controllers.get(id)?.abort();
      patch(id, { status: 'paused' });
    },
    resume: (id) => {
      const u = find(id);
      if (!u || controllers.has(id)) return;
      patch(id, { status: 'starting' });
      run(id);
    },
    remove: (id) => {
      controllers.get(id)?.abort();
      set({ uploads: get().uploads.filter((u) => u.id !== id) });
    },
  };
});
